import { useState, useEffect } from 'react';
import Modal from './Modal';
import '../css/CloseSlotReasonModal.css';
import { SLOT_CLOSE_REASON_MAX_LENGTH } from '../constants/bookingConst';
import { normalizeCloseReason } from '../utils/slotCloseReasonUtil';

/**
 * Asks the admin why a time slot is being closed. Opened from the Manage
 * availability grid when a cell's status is set to Closed; the hour is only
 * staged as closed once a reason is confirmed. Built on the shared Modal, so
 * it has the × close button and ignores clicks outside it.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {string} props.slotLabel - e.g. "Sep 29, 2026 · Court 2 · 6:00 - 7:00 PM"
 * @param {(reason: string) => void} props.onConfirm - receives the trimmed reason
 * @param {() => void} props.onCancel - closes without closing the slot
 * @returns {JSX.Element|null}
 */
function CloseSlotReasonModal({ isOpen, slotLabel, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  // Every opening starts blank — a reason typed for one hour must not carry
  // over to the next cell the admin closes.
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError('');
    }
  }, [isOpen]);

  /**
   * Validates the reason and hands it back to the grid.
   * @returns {void}
   */
  const handleConfirm = () => {
    const cleaned = normalizeCloseReason(reason);
    if (!cleaned) {
      setError('Please enter a reason for closing this time slot.');
      return;
    }
    onConfirm(cleaned);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title="Close time slot"
      type="warning"
      hideIcon
      showCancel
      cancelText="Cancel"
      confirmText="Close slot"
      onConfirm={handleConfirm}
    >
      <div className="csr-body">
        {slotLabel && <p className="csr-slot">{slotLabel}</p>}
        <label className="csr-label" htmlFor="csr-reason">Reason for closing</label>
        <textarea
          id="csr-reason"
          className={`csr-textarea${error ? ' csr-textarea-error' : ''}`}
          rows={3}
          maxLength={SLOT_CLOSE_REASON_MAX_LENGTH}
          placeholder="e.g. Court maintenance, private event, bad weather"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError('');
          }}
          autoFocus
        />
        <div className="csr-meta">
          <span className="csr-error">{error}</span>
          <span className="csr-count">{reason.length}/{SLOT_CLOSE_REASON_MAX_LENGTH}</span>
        </div>
      </div>
    </Modal>
  );
}

export default CloseSlotReasonModal;
